import React from 'react';

/**
 * 헤더 병합용 셀 정의
 * @typedef {Object} HeaderCell
 * @property {string} label - 표시 텍스트
 * @property {number} [colSpan=1] - 가로 병합 칸 수
 * @property {number} [rowSpan=1] - 세로 병합 칸 수
 * @property {string} [align] - 정렬 'left'|'center'|'right'
 */

/**
 * headerRows를 2D 그리드로 변환 (rowSpan/colSpan 반영)
 * - grid[row][col] === null 이면 위/왼쪽 셀의 병합 영역이므로 렌더 시 스킵
 * - grid[row][col] === { label, colSpan, rowSpan } 이면 해당 셀의 원점(왼쪽 상단)
 *
 * @param {HeaderCell[][]} headerRows - 각 행의 셀 배열 (왼쪽부터 배치, rowSpan/colSpan으로 빈 자리 건너뜀)
 * @param {number} totalColumns - 마지막 행이 채워야 할 총 컬럼 수 (데이터 컬럼 수와 일치해야 함)
 * @returns {{ grid: (HeaderCell|null)[][], columnCount: number } | null} - 실패 시 null
 */
export function buildHeaderGrid(headerRows, totalColumns) {
  if (!headerRows?.length || totalColumns < 1) return null;

  const grid = [];

  for (let r = 0; r < headerRows.length; r++) {
    const rowCells = headerRows[r];
    if (!grid[r]) grid[r] = [];

    for (let i = 0; i < rowCells.length; i++) {
      const cell = rowCells[i];
      const colSpan = Math.max(1, cell.colSpan ?? 1);
      const rowSpan = Math.max(1, cell.rowSpan ?? 1);

      // 이 행에서 비어 있는 첫 컬럼 찾기 (undefined인 칸, null은 위/왼쪽 셀에 의해 덮인 칸)
      let col = 0;
      while (col < totalColumns && grid[r][col] !== undefined) {
        col++;
      }
      if (col + colSpan > totalColumns) return null;

      const cellInfo = {
        label: cell.label ?? '',
        colSpan,
        rowSpan,
        align: cell.align,
      };
      grid[r][col] = cellInfo;

      for (let rr = r; rr < r + rowSpan; rr++) {
        if (!grid[rr]) grid[rr] = [];
        for (let cc = col; cc < col + colSpan; cc++) {
          if (rr === r && cc === col) continue;
          grid[rr][cc] = null;
        }
      }
    }
  }

  // 마지막 행이 totalColumns만큼 채워졌는지 검사 (undefined가 없어야 함)
  const lastRow = grid[grid.length - 1];
  if (!lastRow) return null;
  for (let c = 0; c < totalColumns; c++) {
    if (lastRow[c] === undefined) return null;
  }
  return { grid, columnCount: totalColumns };
}

/**
 * 병합 헤더 그리드를 <thead>용 React 노드 배열로 렌더 (tr 목록)
 *
 * @param {{ grid: (HeaderCell|null)[][], columnCount: number }} headerGrid - buildHeaderGrid 결과
 * @param {Array<{ getSize?: () => number, size?: number }>} columns - 컬럼 정의 (width용, leaf 순서)
 * @param {string} headerAlign - 기본 헤더 정렬
 * @param {React.ElementType} ThComponent - th 스타일 컴포넌트
 */
export function renderMergedHeaderRows(headerGrid, columns, headerAlign, ThComponent) {
  if (!headerGrid?.grid?.length) return [];
  const { grid, columnCount } = headerGrid;
  const getWidth = (idx) => {
    const col = columns?.[idx];
    return (typeof col?.getSize === 'function' ? col.getSize() : col?.size) ?? 150;
  };
  const rows = [];

  for (let r = 0; r < grid.length; r++) {
    const rowCells = [];
    let col = 0;
    while (col < columnCount) {
      const cell = grid[r][col];
      if (cell == null) {
        col++;
        continue;
      }
      const { label, colSpan, rowSpan } = cell;
      const align = cell.align ?? headerAlign;
      let width = 0;
      for (let cc = col; cc < col + colSpan; cc++) width += getWidth(cc);
      rowCells.push(
        React.createElement(
          ThComponent,
          {
            key: `h-${r}-${col}`,
            rowSpan: rowSpan > 1 ? rowSpan : undefined,
            colSpan: colSpan > 1 ? colSpan : undefined,
            style: {
              width: width || undefined,
              minWidth: width || undefined,
              textAlign: align,
              fontWeight: 700, // 병합 헤더: 600은 해당 폰트에서 얇게 보일 수 있어 700(bold)로 명시
            },
          },
          label
        )
      );
      col += cell.colSpan ?? 1;
    }
    rows.push(React.createElement('tr', { key: `tr-${r}` }, rowCells));
  }
  return rows;
}
